import {ActionIcon, Button, Group, Modal, Tooltip, createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {StudyPhase} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ReviewStatus from 'notebook/reviews/ReviewStatus';
import notifications from 'notifications';
import * as StudyDAO from 'studies/StudyDAO';
import CustomStudyDesignEntryModal from 'studies/typed/custom/CustomStudyDesignEntryModal';
import {StudyDesignOverviewProps} from 'studies/typed/utils/TypedStudyTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

type DesignEntry = StudyDesignOverviewProps['study']['designEntry'];

function getPriorDesignEntryName(designEntry: DesignEntry): string | null {
  if (designEntry == null || isInaccessible(designEntry)) {
    return null;
  }
  return designEntry.name;
}

function showPriorEntryStillLinkedToast(priorDesignEntryName: string): void {
  notifications.success(
    `${priorDesignEntryName} has been removed as the design entry for this study but is still associated ` +
      'with this study. You can un-associate it from this study on its metadata tab.'
  );
}

interface NoDesignEntryContentProps {
  canModifyDesignEntry: boolean;
  onAddDesignEntry: () => void;
}

function NoDesignEntryContent({
  canModifyDesignEntry,
  onAddDesignEntry,
}: NoDesignEntryContentProps): JSX.Element {
  return (
    <>
      <p>No design entry.</p>
      {canModifyDesignEntry && (
        <Button
          variant="tertiary"
          size="sm"
          onClick={onAddDesignEntry}
          {...getTestProps({component: 'CustomStudyDesignOverview', element: 'addDesignEntryButton'})}
        >
          Add design entry
        </Button>
      )}
    </>
  );
}

interface DesignEntryActionsProps {
  onReplaceDesignEntry: () => void;
  onRemoveDesignEntry: () => void;
}

function DesignEntryActions({
  onReplaceDesignEntry,
  onRemoveDesignEntry,
}: DesignEntryActionsProps): JSX.Element {
  const {classes} = useDesignEntryActionsStyles();

  return (
    <Group spacing="xs" className={classes.actionsGroup}>
      <Tooltip label="Replace design entry" withArrow={true}>
        <ActionIcon
          onClick={onReplaceDesignEntry}
          aria-label="Replace design entry"
          {...getTestProps({
            component: 'CustomStudyDesignOverview',
            element: 'replaceDesignEntryButton',
          })}
        >
          <BenchlingIcon icon="arrow-right-arrow-left" />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Remove design entry" withArrow={true}>
        <ActionIcon
          onClick={onRemoveDesignEntry}
          aria-label="Remove design entry"
          {...getTestProps({
            component: 'CustomStudyDesignOverview',
            element: 'removeDesignEntryButton',
          })}
        >
          <BenchlingIcon icon="trash-can" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

// ActionIcon centers its icon within a fixed-size box, so the icon glyphs sit visually inset from the
// button's own edge. Pull the whole group left so the icons line up with the "Actions" header text above
// them instead of the (larger, invisible) button box.
const useDesignEntryActionsStyles = createStyles((theme) => ({
  actionsGroup: {
    marginLeft: `-${theme.spacing.sm}`,
  },
}));

interface DesignEntryTableProps {
  designEntry: NonNullable<DesignEntry>;
  canModifyDesignEntry: boolean;
  onReplaceDesignEntry: () => void;
  onRemoveDesignEntry: () => void;
}

function DesignEntryTable({
  designEntry,
  canModifyDesignEntry,
  onReplaceDesignEntry,
  onRemoveDesignEntry,
}: DesignEntryTableProps): JSX.Element {
  const {classes} = useStyles();

  const review =
    !isInaccessible(designEntry) && designEntry.review
      ? {
          ...designEntry.review,
          authors: designEntry.review.authors,
          auditors: designEntry.review.auditorsJson,
          reviewLifecycleVersion: designEntry.review.reviewLifecycleVersion || undefined,
        }
      : undefined;

  return (
    <table className={classes.designTable}>
      <thead>
        <tr>
          <th className={classes.designTableTh}>Study design entry</th>
          <th className={classes.designTableTh}>Actions</th>
          <th className={classes.designTableThRight}>Design entry status</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <BenchChip
              id={isInaccessible(designEntry) ? designEntry.invalidId : designEntry.id}
              size={ChipSize.MINI}
            />
          </td>
          <td>
            {canModifyDesignEntry && (
              <DesignEntryActions
                onReplaceDesignEntry={onReplaceDesignEntry}
                onRemoveDesignEntry={onRemoveDesignEntry}
              />
            )}
          </td>
          <td className={classes.designTableTdRight}>
            {isInaccessible(designEntry) ? (
              <p>You do not have permission to view the review status of the design entry.</p>
            ) : (
              <ReviewStatus review={review} />
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

interface RemoveDesignEntryConfirmationModalProps {
  opened: boolean;
  removalRequiresReturnToDesign: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function RemoveDesignEntryConfirmationModal({
  opened,
  removalRequiresReturnToDesign,
  onCancel,
  onConfirm,
}: RemoveDesignEntryConfirmationModalProps): JSX.Element {
  return (
    <Modal opened={opened} title="Remove design entry" onClose={onCancel}>
      {removalRequiresReturnToDesign ? (
        <p>
          Removing the design entry will move the study back to the design phase. Do you want to continue?
        </p>
      ) : (
        <p>Remove the design entry from this study?</p>
      )}
      <Group position="right">
        <Button variant="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          {...getTestProps({
            component: 'CustomStudyDesignOverview',
            element: 'confirmRemoveDesignEntryButton',
          })}
        >
          Remove
        </Button>
      </Group>
    </Modal>
  );
}

export default function CustomStudyDesignOverview({
  study,
  isEditable,
  refetch,
}: StudyDesignOverviewProps): JSX.Element {
  const [isDesignEntryModalOpen, setIsDesignEntryModalOpen] = useState(false);
  const [isRemoveDesignEntryConfirmOpen, setIsRemoveDesignEntryConfirmOpen] = useState(false);

  const canModifyDesignEntry = isEditable && study.phase !== StudyPhase.COMPLETE;
  const hasDesignEntry = study.designEntry != null;
  const modalActionLabel = hasDesignEntry ? 'Replace design entry' : 'Add design entry';
  // Removing the design entry always invalidates design approval (there's nothing left to approve), so
  // an execution-phase study with required design approval must move back to the design phase.
  // See benchling/studies/domain/study_handler.py:_validate_design_entry_update.
  const removalRequiresReturnToDesign =
    study.phase === StudyPhase.EXECUTION && study.studySchema.isDesignApprovalRequired;

  const handleDesignEntryUpdate = async (
    params: Parameters<React.ComponentProps<typeof CustomStudyDesignEntryModal>['onSubmit']>[0]
  ): Promise<void> => {
    const priorDesignEntryName = getPriorDesignEntryName(study.designEntry);
    await StudyDAO.updateStudy(study.id, params);
    await refetch();
    if (priorDesignEntryName != null) {
      showPriorEntryStillLinkedToast(priorDesignEntryName);
    }
  };

  const handleRemoveDesignEntry = async (): Promise<void> => {
    const priorDesignEntryName = getPriorDesignEntryName(study.designEntry);
    await StudyDAO.updateStudy(study.id, {
      designEntryId: null,
      phase: removalRequiresReturnToDesign ? StudyPhase.DESIGN : undefined,
    });
    await refetch();
    setIsRemoveDesignEntryConfirmOpen(false);
    if (priorDesignEntryName != null) {
      showPriorEntryStillLinkedToast(priorDesignEntryName);
    }
  };

  return (
    <div {...getTestProps({component: 'CustomStudyDesignOverview', element: 'root'})}>
      {study.designEntry == null ? (
        <NoDesignEntryContent
          canModifyDesignEntry={canModifyDesignEntry}
          onAddDesignEntry={() => {
            setIsDesignEntryModalOpen(true);
          }}
        />
      ) : (
        <DesignEntryTable
          designEntry={study.designEntry}
          canModifyDesignEntry={canModifyDesignEntry}
          onReplaceDesignEntry={() => {
            setIsDesignEntryModalOpen(true);
          }}
          onRemoveDesignEntry={() => {
            setIsRemoveDesignEntryConfirmOpen(true);
          }}
        />
      )}
      {isDesignEntryModalOpen && (
        <CustomStudyDesignEntryModal
          studySchemaId={study.studySchema.id}
          studyPhase={study.phase}
          isDesignApprovalRequired={study.studySchema.isDesignApprovalRequired}
          initialEntryFolderId={hasDesignEntry ? undefined : study.folder?.id}
          title={modalActionLabel}
          confirmLabel={modalActionLabel}
          onRequestClose={() => {
            setIsDesignEntryModalOpen(false);
          }}
          onSubmit={handleDesignEntryUpdate}
        />
      )}
      <RemoveDesignEntryConfirmationModal
        opened={isRemoveDesignEntryConfirmOpen}
        removalRequiresReturnToDesign={removalRequiresReturnToDesign}
        onCancel={() => {
          setIsRemoveDesignEntryConfirmOpen(false);
        }}
        onConfirm={handleRemoveDesignEntry}
      />
    </div>
  );
}

const useStyles = createStyles({
  designTable: {
    width: '100%',
    tableLayout: 'fixed',
  },
  designTableTh: {
    paddingBottom: 5,
  },
  designTableThRight: {
    paddingBottom: 5,
    textAlign: 'right',
  },
  designTableTdRight: {
    textAlign: 'right',
  },
});
